<template>
  <component
    :is="as"
    :class="computedClasses"
    :to="to"
    :rel="rel"
    :target="target"
    v-bind="computedAttributes"
    :aria-disabled="disabled"
    :aria-label="hideLabel ? label : undefined"
    tabindex="0"
    @click="handleClick"
  >
    <SbIcon v-if="icon" :name="icon" />
    <span v-if="label && !hideLabel" class="sb-link__label">
      {{ label }}
    </span>
    <span v-else-if="$slots.default" class="sb-link__label">
      <slot />
    </span>
    <SbIcon v-if="iconRight" :name="iconRight" />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SbIcon from '../Icon'
import type { AvailableIcons } from '../../lib/all-icons'

export type SbLinkProps = {
  /**
   * Define the tag of the component. The <a> tag is the default value
   */
  as?: string
  /**
   * Define the label of the link. If the hideLabel is true, the label will be used as aria-label
   */
  label?: string
  /**
   * Define if the label should be hidden. If true, the label will be used as aria-label
   */
  hideLabel?: boolean
  /**
   * Define the href of the link
   */
  href?: string
  /**
   * Define the left icon of the link
   */
  icon?: AvailableIcons
  /**
   * Define the right icon of the link
   */
  iconRight?: AvailableIcons
  /**
   * Define the title of the link
   */
  title?: string
  /**
   * Define the to property to a router-link component
   */
  to?: string | object
  variant?: string
  /**
   * Define the rel of the link
   */
  rel?: string
  /**
   * Define the target of the link
   */
  target?: string
  /**
   * Define if the link is disabled. If true, the link receives the aria-disabled attribute
   */
  disabled?: boolean
  /**
   * Define if the link is subtle
   */
  subtle?: boolean
  /**
   * Define if the link is inverse. Used to create a contrast between the link and the background
   */
  inverse?: boolean
  /**
   * Define if the link is a button
   */
  isButton?: boolean
  /**
   * Define the size of the button
   */
  size?: 'small' | 'normal'
  linkClass?: string
}

const props = withDefaults(defineProps<SbLinkProps>(), {
  as: 'a',
  hideLabel: false,
  disabled: false,
  subtle: false,
  inverse: false,
  isButton: false,
  size: 'normal',
})

const computedClasses = computed(() => ({
  'sb-link--has-icon': props.icon,
  'sb-link--has-icon-right': props.iconRight,
  [`sb-${props.isButton ? 'button' : 'link'}--${props.variant}`]: props.variant,
  'sb-link--subtle': props.subtle,
  'sb-link--inverse': props.inverse,
  'sb-link': !props.isButton,
  'sb-button': props.isButton,
  'sb-button--small': props.size === 'small',
  [props.linkClass || '']: Boolean(props.linkClass),
}))

const computedAttributes = computed(() => {
  if (props.to) {
    return {
      to: props.to,
      title: props.title,
    }
  }
  return {
    href: props.href,
    title: props.title,
  }
})

const handleClick = (event: Event) => {
  if (props.disabled) {
    event.preventDefault()
    event.stopPropagation()
  }
}
</script>
